import * as t from 'io-ts';

import {AnySchemaType, ApiSerializableType, ApiType, TypeOfSchema} from './commonApiTypes';
import {findRuntimeTypeLogger} from './types/ioTsLogger';

/*
 * Common types.
 */

export enum FrameTypesEnum {
  CALENDAR = 'CALENDAR',
  CUSTOMER_VIEW = 'CUSTOMER_VIEW',
  KNOWLEDGE_BASE = 'KNOWLEDGE_BASE',
  ENTRY_POINT = 'ENTRY_POINT',
  INTEGRATION = 'INTEGRATION',
  KEYBOARD_HELP = 'KEYBOARD_HELP',
  MANAGER = 'MANAGER',
  PLUGIN = 'PLUGIN',
  APPLICATION_ENTRY_POINT = 'APPLICATION_ENTRY_POINT',
  AI_SMART_QA = 'AI_SMART_QA',
  ASK_COPILOT = 'ASK_COPILOT',
}

/*
 * ApiType builders.
 */

export function makeApiType<T extends AnySchemaType>(
  name: string,
  runtimeType: T,
  parse: (src: any) => TypeOfSchema<T>,
  dangerouslyTransformRaw?: (src: any) => any,
): ApiType<T> {
  return {
    name,
    runtimeType,
    parse,
    dangerouslyTransformRaw,
    reportError: findRuntimeTypeLogger().error,
  };
}

export function makeApiSerializableType<T extends AnySchemaType>(
  name: string,
  runtimeType: T,
  parse: (src: any) => TypeOfSchema<T>,
  serialize: (src: TypeOfSchema<T>) => any,
  dangerouslyTransformRaw?: (src: any) => any,
): ApiSerializableType<T> {
  return {
    name,
    runtimeType,
    parse,
    serialize,
    dangerouslyTransformRaw,
    reportError: findRuntimeTypeLogger().error,
  };
}

/*
 * Custom runtime type builders.
 */

export class RefType<A, O = A, I = unknown> extends t.Type<A, O, I> {
  constructor(baseType: t.Type<A, O, I>, name: string) {
    super(`frontref-${name}`, baseType.is, baseType.validate, baseType.encode);
  }

  private targetTypeValue: t.Mixed | null = null;

  get targetType() {
    if (this.targetTypeValue === null) {
      throw new Error('Target type not set, make sure to call setTargetType() first in the model file.');
    }
    return this.targetTypeValue;
  }

  setTargetType(targetType: t.Mixed) {
    if (this.targetTypeValue !== null) {
      throw new Error('Target type already set, make sure to call setTargetType() only once.');
    }
    this.targetTypeValue = targetType;
  }
}

function makeIdRefType(name: string) {
  return new RefType(t.Integer, name);
}

function makeUidRefType(name: string) {
  return new RefType(t.string, name);
}

export const RConversationIdRef = makeIdRefType('conversation');
export const RPinnedActivityListIdRef = makeIdRefType('pinnedActivityList');
export const RInquiryListIdRef = makeIdRefType('inquiryList');
export const RSimilarInquiryListIdRef = makeIdRefType('similarInquiryList');
export const RRelatedKnowledgeListIdRef = makeIdRefType('relatedKnowledgeList');
export const RMessageUidRef = makeUidRefType('message');
export const RCommentUidRef = makeUidRefType('comment');
export const RBannerStateUidRef = makeUidRefType('bannerState');
export const RInboxIdRef = makeIdRefType('inbox');
export const RDeletedInboxIdRef = makeIdRefType('deletedInbox');
export const RChannelIdRef = makeIdRefType('channel');
export const RApplicationChannelTypeUidRef = makeUidRefType('applicationChannelType');
export const RTeammateIdRef = makeIdRefType('teammate');
export const RCalendarEventVersionUidRef = makeUidRefType('calendarEventVersion');
export const RCalendarEventUidRef = makeUidRefType('calendarEvent');
export const RContactUidRef = makeUidRefType('contact');
export const RCardIdRef = makeIdRefType('card');
export const RCardGroupIdRef = makeIdRefType('cardGroup');
export const RCardNoteIdRef = makeIdRefType('cardNote');
export const RTagIdRef = makeIdRefType('tag');
export const RDeletedTagIdRef = makeIdRefType('deletedTag');
export const RAccountIdRef = makeIdRefType('account');
export const RTopicIdRef = makeIdRefType('topic');
export const RLocalApplicationIdRef = makeIdRefType('localApplication');
export const RApplicationIdRef = makeIdRefType('application');
export const RApiTokenIdRef = makeIdRefType('apiToken');
export const RAnnotationClientSideUidRef = makeUidRefType('annotationClientSide');
export const RActivityIdRef = makeIdRefType('activity');
export const RCalendarVersionIdRef = makeIdRefType('calendarVersion');
export const REchoIdRef = makeIdRefType('echo');
export const REchoInquiryListIdRef = makeIdRefType('echoInquiryList');
